// src/lib/search.js
//
// Client-side search, filter and sort over the stock list.

const DAY = 86400000;
export const NEW_DAYS = 14;

export function isNew(item) {
  return Date.now() - new Date(item.created_at).getTime() < NEW_DAYS * DAY;
}

// Every word typed has to appear somewhere on the unit, in any order, so
// "samsung display" finds a Samsung in the Display category.
export function matches(item, q) {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;

  const hay = [
    item.title,
    item.make,
    item.model,
    item.category,
    item.condition_note,
    item.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return terms.every((t) => hay.includes(t));
}

function priceOf(item) {
  const n = Number(item.price);
  return item.price_on_request || !Number.isFinite(n) || n <= 0 ? null : n;
}

// "featured" is the order the office drags into place in the portal
export const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest first" },
  { value: "price-asc", label: "Price, low to high" },
  { value: "price-desc", label: "Price, high to low" },
];

// Price on request always sorts last on a price sort - it is not cheap and
// it is not expensive, it is unknown.
export function sortItems(items, sort) {
  const list = [...items];

  if (sort === "price-asc" || sort === "price-desc") {
    const dir = sort === "price-asc" ? 1 : -1;
    return list.sort((a, b) => {
      const pa = priceOf(a);
      const pb = priceOf(b);
      if (pa === null && pb === null) return 0;
      if (pa === null) return 1;
      if (pb === null) return -1;
      return (pa - pb) * dir;
    });
  }

  if (sort === "newest") {
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  // Featured: the portal order, newest first where two share a position
  return list.sort(
    (a, b) =>
      (a.sort_order || 0) - (b.sort_order || 0) ||
      new Date(b.created_at) - new Date(a.created_at)
  );
}

// Counts per category for the chips, taken from whatever the search matches,
// so the numbers always describe what a click on the chip will show.
//
// `order` is the category list from the API, in the order set in the portal.
// Anything not in it (a page loaded before a rename) falls to the end
// alphabetically.
export function categoryCounts(items, order = []) {
  const map = new Map();
  for (const it of items) {
    if (!it.category) continue;
    map.set(it.category, (map.get(it.category) || 0) + 1);
  }

  const rank = (name) => {
    const i = order.indexOf(name);
    return i === -1 ? Infinity : i;
  };

  return [...map.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([category, count]) => ({ category, count }));
}